/**
 * To match accessibility requirement, we always provide an input in the component.
 * Other element will not set `tabIndex` to avoid `onBlur` sequence problem.
 * For focused select, we set `aria-live="polite"` to update the accessibility content.
 *
 * ref:
 * - keyboard: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/listbox_role#Keyboard_interactions
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { useState, useRef, useEffect, useMemo } from 'react';
import KeyCode from 'rc-util/lib/KeyCode';
import classNames from 'classnames';
import useMergedState from 'rc-util/lib/hooks/useMergedState';
import Selector from './Selector';
import SelectTrigger from './SelectTrigger';
import { INTERNAL_PROPS_MARK } from './interface/generator';
import { toInnerValue, toOuterValues, removeLastEnabledValue, getUUID } from './utils/commonUtil';
import TransBtn from './TransBtn';
import getIcons from '../utils/iconUtil';
import useLock from './hooks/useLock';
import useDelayReset from './hooks/useDelayReset';
import useLayoutEffect from './hooks/useLayoutEffect';
import { getSeparatedContent } from './utils/valueUtil';
import useSelectTriggerControl from './hooks/useSelectTriggerControl';
import useCacheDisplayValue from './hooks/useCacheDisplayValue';
import useCacheOptions from './hooks/useCacheOptions';
const DEFAULT_OMIT_PROPS = [
    'removeIcon',
    'placeholder',
    'autoFocus',
    'maxTagCount',
    'maxTagTextLength',
    'maxTagPlaceholder',
    'choiceTransitionName',
    'onInputKeyDown',
    'tabIndex'
];
/**
 * This function is in internal usage.
 * Do not use it in your prod env since we may refactor this.
 */
export default function generateSelector(config) {
    const { prefixCls: defaultPrefixCls, components: { optionList: OptionList }, convertChildrenToData, flattenOptions, getLabeledValue, filterOptions, isValueDisabled, findValueOption, warningProps, fillOptionsWithMissingValue, omitDOMProps } = config;
    // Use raw define since `React.FC` not support generic
    function Select(props, ref) {
        const { prefixCls = defaultPrefixCls, className, id, open, defaultOpen, options, children, mode, value, defaultValue, labelInValue, showAllSelected = false, 
        // Search related
        showSearch, inputValue, searchValue, filterOption, filterSort, optionFilterProp = 'value', 
        // optionFilterProp = 'children',
        autoClearSearchValue = true, onSearch, 
        // Icons
        suffixIconPre, removeIcon, allowClear, clearIcon, showArrow, menuItemSelectedIcon, 
        // Others
        disabled, loading, defaultActiveFirstOption, notFoundContent, optionLabelProp, backfill, tabIndex, getInputElement, getPopupContainer, 
        // Dropdown
        listHeight = 200, listItemHeight = 20, animation, transitionName, virtual, dropdownStyle, dropdownClassName, dropdownMatchSelectWidth, dropdownRender, dropdownAlign, showAction = [], showTitle, showSelectAll, direction, placement, popupOverflow, 
        // Tags
        tokenSeparators, groupSelectorRender, tagRender, optionRender, renderEmpty, keepExpand, defaultExpandGroupKey, 
        // Events
        onPopupScroll, onDropdownVisibleChange, onFocus, onBlur, onKeyUp, onKeyDown, onMouseDown, onChange, onSelect, onDeselect, onClear, onExpandChange, internalProps = {} } = props, restProps = __rest(props, ["prefixCls", "className", "id", "open", "defaultOpen", "options", "children", "mode", "value", "defaultValue", "labelInValue", "showAllSelected", "showSearch", "inputValue", "searchValue", "filterOption", "filterSort", "optionFilterProp", "autoClearSearchValue", "onSearch", "suffixIconPre", "removeIcon", "allowClear", "clearIcon", "showArrow", "menuItemSelectedIcon", "disabled", "loading", "defaultActiveFirstOption", "notFoundContent", "optionLabelProp", "backfill", "tabIndex", "getInputElement", "getPopupContainer", "listHeight", "listItemHeight", "animation", "transitionName", "virtual", "dropdownStyle", "dropdownClassName", "dropdownMatchSelectWidth", "dropdownRender", "dropdownAlign", "showAction", "showTitle", "showSelectAll", "direction", "placement", "popupOverflow", "tokenSeparators", "groupSelectorRender", "tagRender", "optionRender", "renderEmpty", "keepExpand", "defaultExpandGroupKey", "onPopupScroll", "onDropdownVisibleChange", "onFocus", "onBlur", "onKeyUp", "onKeyDown", "onMouseDown", "onChange", "onSelect", "onDeselect", "onClear", "onExpandChange", "internalProps"]);
        const useInternalProps = internalProps.mark === INTERNAL_PROPS_MARK;
        const domProps = omitDOMProps ? omitDOMProps(restProps) : restProps;
        DEFAULT_OMIT_PROPS.forEach(prop => {
            delete domProps[prop];
        });
        const containerRef = useRef(null);
        const triggerRef = useRef(null);
        const selectorRef = useRef(null);
        const listRef = useRef(null);
        const tokenWithEnter = useMemo(() => (tokenSeparators || []).some(tokenSeparator => ['\n', '\r\n'].includes(tokenSeparator)), [tokenSeparators]);
        /** Used for component focused management */
        const [mockFocused, setMockFocused, cancelSetMockFocused] = useDelayReset();
        // Inner id for accessibility usage. Only work in client side
        const [innerId, setInnerId] = useState();
        useEffect(() => {
            setInnerId(`rc_select_${getUUID()}`);
        }, []);
        const mergedId = id || innerId;
        // optionLabelProp
        let mergedOptionLabelProp = optionLabelProp;
        if (mergedOptionLabelProp === undefined) {
            mergedOptionLabelProp = options ? 'label' : 'children';
        }
        // labelInValue
        const mergedLabelInValue = mode === 'combobox' ? false : labelInValue;
        const isMultiple = mode === 'tags' || mode === 'multiple';
        const mergedShowSearch = showSearch !== undefined ? showSearch : isMultiple || mode === 'combobox';
        // ============================== Ref ===============================
        const selectorDomRef = useRef(null);
        React.useImperativeHandle(ref, () => {
            var _a, _b, _c;
            return ({
                focus: (_a = selectorRef.current) === null || _a === void 0 ? void 0 : _a.focus,
                blur: (_b = selectorRef.current) === null || _b === void 0 ? void 0 : _b.blur,
                scrollTo: (_c = listRef.current) === null || _c === void 0 ? void 0 : _c.scrollTo
            });
        });
        // ============================= Value ==============================
        const [mergedValue, setMergedValue] = useMergedState(defaultValue, {
            value
        });
        /** Unique raw values */
        const [mergedRawValue, mergedValueMap] = useMemo(() => toInnerValue(mergedValue, {
            labelInValue: mergedLabelInValue,
            combobox: mode === 'combobox'
        }), [mergedValue, mergedLabelInValue]);
        /** We cache a set of raw values to speed up check */
        const rawValues = useMemo(() => new Set(mergedRawValue), [mergedRawValue]);
        // ============================= Option =============================
        // Set by option list active, it will merge into search input when mode is `combobox`
        const [activeValue, setActiveValue] = useState(null);
        const [innerSearchValue, setInnerSearchValue] = useState('');
        let mergedSearchValue = innerSearchValue;
        if (mode === 'combobox' && mergedValue !== undefined) {
            mergedSearchValue = mergedValue;
        }
        else if (searchValue !== undefined) {
            mergedSearchValue = searchValue;
        }
        else if (inputValue) {
            mergedSearchValue = inputValue;
        }
        const mergedOptions = useMemo(() => {
            let newOptions = options;
            if (newOptions === undefined) {
                newOptions = convertChildrenToData(children);
            }
            /**
       * `tags` should fill un-list item.
       * This is not cool here since TreeSelect do not need this
       */
            if (mode === 'tags' && fillOptionsWithMissingValue) {
                newOptions = fillOptionsWithMissingValue(newOptions, mergedValue, mergedOptionLabelProp, labelInValue);
            }
            return newOptions || [];
        }, [options, children, mode, mergedValue]);
        const mergedFlattenOptions = useMemo(() => flattenOptions(mergedOptions, props), [mergedOptions]);
        const getValueOption = useCacheOptions(mergedFlattenOptions);
        // Display options for OptionList
        const displayOptions = useMemo(() => {
            if (!mergedSearchValue || !mergedShowSearch) {
                return [...mergedOptions];
            }
            const filteredOptions = filterOptions(mergedSearchValue, mergedOptions, {
                optionFilterProp,
                filterOption: mode === 'combobox' && filterOption === undefined ? () => true : filterOption
            });
            if (mode === 'tags'
                && filteredOptions.every(opt => opt[optionFilterProp] !== mergedSearchValue)) {
                filteredOptions.unshift({
                    value: mergedSearchValue,
                    label: mergedSearchValue,
                    key: '__RC_SELECT_TAG_PLACEHOLDER__'
                });
            }
            if (filterSort && Array.isArray(filteredOptions)) {
                return [...filteredOptions].sort(filterSort);
            }
            return filteredOptions;
        }, [mergedOptions, mergedSearchValue, mode, mergedShowSearch, filterSort]);
        const displayFlattenOptions = useMemo(() => flattenOptions(displayOptions, props), [displayOptions]);
        useEffect(() => {
            if (listRef.current && listRef.current.scrollTo) {
                listRef.current.scrollTo(0);
            }
        }, [mergedSearchValue]);
        // ============================ Selector ============================
        let displayValues = useMemo(() => {
            const tmpValues = mergedRawValue.map((val) => {
                const valueOptions = getValueOption([val]);
                const displayValue = getLabeledValue(val, {
                    options: valueOptions,
                    prevValueMap: mergedValueMap,
                    labelInValue: mergedLabelInValue,
                    optionLabelProp: mergedOptionLabelProp,
                    groupSelectorRender
                });
                return Object.assign(Object.assign({}, displayValue), { disabled: isValueDisabled(val, valueOptions) });
            });
            if (!mode
                && tmpValues.length === 1
                && tmpValues[0].value === null
                && tmpValues[0].label === null) {
                return [];
            }
            return tmpValues;
        }, [mergedValue, mergedOptions, mode]);
        // Polyfill with cache label
        displayValues = useCacheDisplayValue(displayValues);
        const triggerSelect = (newValue, isSelect, source) => {
            const newValueOption = getValueOption([newValue]);
            const outOption = findValueOption([newValue], newValueOption)[0];
            if (!internalProps.skipTriggerSelect) {
                // Skip trigger `onSelect` or `onDeselect` if configured
                const selectValue = (mergedLabelInValue
                    ? getLabeledValue(newValue, {
                        options: newValueOption,
                        prevValueMap: mergedValueMap,
                        labelInValue: mergedLabelInValue,
                        optionLabelProp: mergedOptionLabelProp
                    })
                    : newValue);
                if (isSelect && onSelect) {
                    onSelect(selectValue, outOption);
                }
                else if (!isSelect && onDeselect) {
                    onDeselect(selectValue, outOption);
                }
            }
            // Trigger internal event
            if (useInternalProps) {
                if (isSelect && internalProps.onRawSelect) {
                    internalProps.onRawSelect(newValue, outOption, source);
                }
                else if (!isSelect && internalProps.onRawDeselect) {
                    internalProps.onRawDeselect(newValue, outOption, source);
                }
            }
        };
        // We need cache options here in case user update the option list
        const [prevValueOptions, setPrevValueOptions] = useState([]);
        const triggerChange = (newRawValues) => {
            if (useInternalProps && internalProps.skipTriggerChange) {
                return;
            }
            const newRawValuesOptions = getValueOption(newRawValues);
            const outValues = toOuterValues(Array.from(newRawValues), {
                labelInValue: mergedLabelInValue,
                options: newRawValuesOptions,
                getLabeledValue,
                prevValueMap: mergedValueMap,
                optionLabelProp: mergedOptionLabelProp
            });
            const outValue = (isMultiple ? outValues : outValues[0]);
            // Skip trigger if prev & current value is both empty
            if (onChange && (mergedRawValue.length !== 0 || outValues.length !== 0)) {
                const outOptions = findValueOption(newRawValues, newRawValuesOptions, { prevValueOptions });
                // We will cache option in case it removed by ajax
                setPrevValueOptions(outOptions.map((option, index) => {
                    const clone = Object.assign({}, option);
                    Object.defineProperty(clone, '_INTERNAL_OPTION_VALUE_', {
                        get: () => newRawValues[index]
                    });
                    return clone;
                }));
                onChange(outValue, isMultiple ? outOptions : outOptions[0]);
            }
            setMergedValue(outValue);
        };
        const onInternalSelect = (newValue, { selected, source }) => {
            if (disabled) {
                return;
            }
            let newRawValue;
            if (isMultiple) {
                newRawValue = new Set(mergedRawValue);
                if (selected) {
                    newRawValue.add(newValue);
                }
                else {
                    newRawValue.delete(newValue);
                }
            }
            else {
                newRawValue = new Set();
                newRawValue.add(newValue);
            }
            // Multiple always trigger change and single should change if value changed
            if (isMultiple || (!isMultiple && Array.from(mergedRawValue)[0] !== newValue)) {
                triggerChange(Array.from(newRawValue));
            }
            // Trigger `onSelect`. Single mode always trigger select
            triggerSelect(newValue, !isMultiple || selected, source);
            // Clean search value if single or configured
            if (mode === 'combobox') {
                setInnerSearchValue(String(newValue));
                setActiveValue('');
            }
            else if (!isMultiple || autoClearSearchValue) {
                setInnerSearchValue('');
                setActiveValue('');
            }
        };
        const onInternalOptionSelect = (newValue, info) => {
            onInternalSelect(newValue, Object.assign(Object.assign({}, info), { source: 'option' }));
        };
        const onInternalSelectionSelect = (newValue, info) => {
            onInternalSelect(newValue, Object.assign(Object.assign({}, info), { source: 'selection' }));
        };
        // ============================= Input ==============================
        // Only works in `combobox`
        const customizeInputElement = (mode === 'combobox' && getInputElement && getInputElement()) || null;
        // ============================== Open ==============================
        const [innerOpen, setInnerOpen] = useMergedState(undefined, {
            defaultValue: defaultOpen,
            value: open
        });
        let mergedOpen = innerOpen;
        // Not trigger `open` in `combobox` when `notFoundContent` is empty
        const emptyListContent = !notFoundContent && !displayOptions.length;
        if (disabled || (emptyListContent && mergedOpen && mode === 'combobox')) {
            mergedOpen = false;
        }
        // const triggerOpen = emptyListContent ? false : mergedOpen;
        const triggerOpen = mergedOpen;
        const onToggleOpen = (newOpen) => {
            const nextOpen = newOpen !== undefined ? newOpen : !mergedOpen;
            if (innerOpen !== nextOpen && !disabled) {
                setInnerOpen(nextOpen);
                if (onDropdownVisibleChange) {
                    onDropdownVisibleChange(nextOpen);
                }
            }
        };
        // ===================== Icons =====================
        const { suffixIcon: inputIcon } = getIcons({
            suffixIcon: suffixIconPre,
            clearIcon,
            menuItemSelectedIcon,
            removeIcon,
            loading,
            prefixCls,
            multiple: isMultiple,
            triggerOpen
        });
        useSelectTriggerControl([containerRef.current, triggerRef.current && triggerRef.current.getPopupElement()], triggerOpen, onToggleOpen);
        // ============================= Search =============================
        const triggerSearch = (searchText, fromTyping, isCompositing) => {
            let ret = true;
            let newSearchText = searchText;
            setActiveValue(null);
            // Check if match the `tokenSeparators`
            const patchLabels = isCompositing
                ? null
                : getSeparatedContent(searchText, tokenSeparators);
            let patchRawValues = patchLabels;
            if (mode === 'combobox') {
                // Only typing will trigger onChange
                if (fromTyping) {
                    triggerChange([newSearchText]);
                }
            }
            else if (patchLabels) {
                newSearchText = '';
                if (mode !== 'tags') {
                    patchRawValues = patchLabels
                        .map(label => {
                        const item = mergedFlattenOptions.find(({ data }) => data[mergedOptionLabelProp] === label);
                        return item ? item.data.value : null;
                    })
                        .filter((val) => val !== null);
                }
                const newRawValues = Array.from(new Set([...mergedRawValue, ...patchRawValues]));
                triggerChange(newRawValues);
                newRawValues.forEach(newRawValue => {
                    triggerSelect(newRawValue, true, 'input');
                });
                // Should close when paste finish
                onToggleOpen(false);
                // Tell Selector that break next actions
                ret = false;
            }
            setInnerSearchValue(newSearchText);
            if (onSearch && mergedSearchValue !== newSearchText) {
                onSearch(newSearchText);
            }
            return ret;
        };
        // Only triggered when menu is closed & mode is tags
        // If menu is open, OptionList will take charge
        // If mode isn't tags, press enter is not meaningful when you can't see any option
        const onSearchSubmit = (searchText) => {
            const newRawValues = Array.from(new Set([...mergedRawValue, searchText]));
            triggerChange(newRawValues);
            newRawValues.forEach(newRawValue => {
                triggerSelect(newRawValue, true, 'input');
            });
            setInnerSearchValue('');
        };
        // Close dropdown when disabled change
        useEffect(() => {
            if (innerOpen && !!disabled) {
                setInnerOpen(false);
            }
        }, [disabled]);
        // Close will clean up single mode search text
        useEffect(() => {
            if (!mergedOpen && !isMultiple && mode !== 'combobox') {
                triggerSearch('', false, false);
            }
        }, [mergedOpen]);
        // ============================ Keyboard ============================
        /**
         * We record input value here to check if can press to clean up by backspace
         * - null: Key is not down, this is reset by key up
         * - true: Search text is empty when first time backspace down
         * - false: Search text is not empty when first time backspace down
         */
        const [getClearLock, setClearLock] = useLock();
        // KeyDown
        const onInternalKeyDown = (event, ...rest) => {
            const clearLock = getClearLock();
            const { which } = event;
            // We only manage open state here, close logic should handle by list component
            if (!mergedOpen && which === KeyCode.ENTER) {
                onToggleOpen(true);
            }
            setClearLock(!!mergedSearchValue);
            // Remove value by `backspace`
            if (which === KeyCode.BACKSPACE
                && !clearLock
                && isMultiple
                && !mergedSearchValue
                && mergedRawValue.length) {
                const removeInfo = removeLastEnabledValue(displayValues, mergedRawValue);
                if (removeInfo.removedValue !== null) {
                    triggerChange(removeInfo.values);
                    triggerSelect(removeInfo.removedValue, false, 'input');
                }
            }
            if (mergedOpen && listRef.current) {
                listRef.current.onKeyDown(event, ...rest);
            }
            if (onKeyDown) {
                onKeyDown(event, ...rest);
            }
        };
        // KeyUp
        const onInternalKeyUp = (event, ...rest) => {
            if (mergedOpen && listRef.current) {
                listRef.current.onKeyUp(event, ...rest);
            }
            if (onKeyUp) {
                onKeyUp(event, ...rest);
            }
        };
        // ========================== Focus / Blur ==========================
        /** Record real focus status */
        const focusRef = useRef(false);
        const onContainerFocus = (...args) => {
            setMockFocused(true);
            if (!disabled) {
                if (onFocus && !focusRef.current) {
                    onFocus(...args);
                }
                // `showAction` should handle `focus` if set
                if (showAction.includes('focus')) {
                    onToggleOpen(true);
                }
            }
            focusRef.current = true;
        };
        const onContainerBlur = (...args) => {
            setMockFocused(false, () => {
                focusRef.current = false;
                onToggleOpen(false);
            });
            if (disabled) {
                return;
            }
            if (mergedSearchValue) {
                // `tags` mode should move `searchValue` into values
                if (mode === 'tags') {
                    triggerSearch('', false, false);
                    triggerChange(Array.from(new Set([...mergedRawValue, mergedSearchValue])));
                }
                // else if (mode === 'multiple') {
                //     // `multiple` mode only clean the search value but not trigger event
                //     setTimeout(() => {
                //         setInnerSearchValue('');
                //     }, 100);
                // }
            }
            if (onBlur) {
                onBlur(...args);
            }
        };
        const activeTimeoutIds = [];
        useEffect(() => () => {
            activeTimeoutIds.forEach(timeoutId => clearTimeout(timeoutId));
            activeTimeoutIds.splice(0, activeTimeoutIds.length);
        }, []);
        const onInternalMouseDown = (event, ...restArgs) => {
            const { target } = event;
            const popupElement = triggerRef.current && triggerRef.current.getPopupElement();
            // We should give focus back to selector if clicked item is not focusable
            if (popupElement && popupElement.contains(target)) {
                const timeoutId = setTimeout(() => {
                    const index = activeTimeoutIds.indexOf(timeoutId);
                    if (index !== -1) {
                        activeTimeoutIds.splice(index, 1);
                    }
                    cancelSetMockFocused();
                    if (!popupElement.contains(document.activeElement)) {
                        selectorRef.current.focus();
                    }
                });
                activeTimeoutIds.push(timeoutId);
            }
            if (onMouseDown) {
                onMouseDown(event, ...restArgs);
            }
        };
        // ========================= Accessibility ==========================
        const [accessibilityIndex, setAccessibilityIndex] = useState(0);
        const mergedDefaultActiveFirstOption = defaultActiveFirstOption !== undefined ? defaultActiveFirstOption : mode !== 'combobox';
        const onActiveValue = (active, index, { source = 'keyboard' } = {}) => {
            setAccessibilityIndex(index);
            if (backfill && mode === 'combobox' && active !== null && source === 'keyboard') {
                setActiveValue(String(active));
            }
        };
        // ============================= Popup ==============================
        const [containerWidth, setContainerWidth] = useState(null);
        const [, forceUpdate] = useState({});
        // We need force update here since popup dom is render async
        function onPopupMouseEnter() {
            forceUpdate({});
        }
        useLayoutEffect(() => {
            if (triggerOpen) {
                const newWidth = Math.ceil(containerRef.current.offsetWidth);
                if (containerWidth !== newWidth) {
                    setContainerWidth(newWidth);
                }
            }
        }, [triggerOpen]);
        function cleanSearchValue() {
            setInnerSearchValue('');
        }
        const popupNode = (<OptionList ref={listRef} prefixCls={prefixCls} id={mergedId} open={mergedOpen} childrenAsData={!options} options={displayOptions} flattenOptions={displayFlattenOptions} multiple={isMultiple} values={rawValues} height={listHeight} itemHeight={listItemHeight} onSelect={onInternalOptionSelect} onToggleOpen={onToggleOpen} onActiveValue={onActiveValue} defaultActiveFirstOption={mergedDefaultActiveFirstOption} notFoundContent={notFoundContent} renderEmpty={renderEmpty} keepExpand={keepExpand} defaultExpandGroupKey={defaultExpandGroupKey} onExpandChange={onExpandChange} onScroll={onPopupScroll} searchValue={mergedSearchValue} menuItemSelectedIcon={menuItemSelectedIcon} virtual={virtual !== false && dropdownMatchSelectWidth !== false} onMouseEnter={onPopupMouseEnter} triggerChange={triggerChange} setInnerOpen={setInnerOpen} showTitle={showTitle} showSelectAll={showSelectAll} optionRender={optionRender} cleanSearchValue={cleanSearchValue}/>);
        // ============================= Clear ==============================
        let clearNode;
        const onClearMouseDown = () => {
            // Trigger internal `onClear` event
            if (useInternalProps && internalProps.onClear) {
                internalProps.onClear();
            }
            if (onClear) {
                onClear();
            }
            const disabledValues = displayValues.filter(value => !!value.disabled);
            triggerChange(disabledValues.map(v => v.value));
            triggerSearch('', false, false);
        };
        if (!disabled && allowClear && (mergedRawValue.length || mergedSearchValue)) {
            clearNode = (<TransBtn className={`${prefixCls}-clear`} onMouseDown={onClearMouseDown} customizeIcon={clearIcon}>
          ×
                </TransBtn>);
        }
        // ============================= Arrow ==============================
        const mergedShowArrow = showArrow !== undefined ? showArrow : loading || mode !== 'combobox';
        let arrowNode;
        if (mergedShowArrow) {
            arrowNode = (<TransBtn className={classNames(`${prefixCls}-arrow`, {
                    [`${prefixCls}-arrow-loading`]: loading
                })} customizeIcon={inputIcon} customizeIconProps={{
                    loading,
                    searchValue: mergedSearchValue,
                    open: mergedOpen,
                    focused: mockFocused,
                    showSearch: mergedShowSearch
                }}/>);
        }
        // ============================ Warning =============================
        if (process.env.NODE_ENV !== 'production' && warningProps) {
            warningProps(props);
        }
        // ============================= Render =============================
        const mergedClassName = classNames(prefixCls, className, {
            [`${prefixCls}-focused`]: mockFocused,
            [`${prefixCls}-multiple`]: isMultiple,
            [`${prefixCls}-single`]: !isMultiple,
            [`${prefixCls}-allow-clear`]: allowClear,
            [`${prefixCls}-show-arrow`]: mergedShowArrow,
            [`${prefixCls}-disabled`]: disabled,
            [`${prefixCls}-loading`]: loading,
            [`${prefixCls}-open`]: mergedOpen,
            [`${prefixCls}-customize-input`]: customizeInputElement,
            [`${prefixCls}-show-search`]: mergedShowSearch
        });
        // mode为multiple时，且属性showAllSelected为true，并且全选时展示全部
        // 修复筛选时bug
        const showAllSelectedTag = mode === 'multiple'
            && showAllSelected
            && displayOptions.length === mergedOptions.length
            && displayOptions.length === displayValues.length && displayValues.length > 0;
        return (<div className={mergedClassName} {...domProps} ref={containerRef} onMouseDown={onInternalMouseDown} onKeyDown={onInternalKeyDown} onKeyUp={onInternalKeyUp} onFocus={onContainerFocus} onBlur={onContainerBlur}>
                {mockFocused && !mergedOpen && (<span style={{
                    width: 0,
                    height: 0,
                    display: 'flex',
                    overflow: 'hidden',
                    opacity: 0
                }} aria-live="polite">
                        {/* Merge into one string to make screen reader work as expect */}
                        {`${mergedRawValue.join(', ')}`}
                    </span>)}
                <SelectTrigger ref={triggerRef} disabled={disabled} prefixCls={prefixCls} visible={triggerOpen} popupElement={popupNode} popupOverflow={popupOverflow} multiple={isMultiple} containerWidth={containerWidth} animation={animation} transitionName={transitionName} dropdownStyle={dropdownStyle} dropdownClassName={dropdownClassName} direction={direction} dropdownMatchSelectWidth={dropdownMatchSelectWidth} dropdownRender={dropdownRender} dropdownAlign={dropdownAlign} getPopupContainer={getPopupContainer} empty={!mergedOptions.length} placement={placement} getTriggerDOMNode={() => selectorDomRef.current}>
                    <Selector {...props} domRef={selectorDomRef} prefixCls={prefixCls} inputElement={customizeInputElement} ref={selectorRef} id={mergedId} showSearch={mergedShowSearch} mode={mode} accessibilityIndex={accessibilityIndex} multiple={isMultiple} tagRender={tagRender} values={displayValues} showAllSelectedTag={showAllSelectedTag} open={mergedOpen} onToggleOpen={onToggleOpen} searchValue={mergedSearchValue} activeValue={activeValue} onSearch={triggerSearch} onSearchSubmit={onSearchSubmit} onSelect={onInternalSelectionSelect} onClearMouseDown={onClearMouseDown} tokenWithEnter={tokenWithEnter}/>
                </SelectTrigger>
                {arrowNode}
                {clearNode}
            </div>);
    }
    const RefSelect = React.forwardRef(Select);
    return RefSelect;
}
